import { useEffect, useRef, useState } from "react"
import * as pdfjsLib from "pdfjs-dist"

if (typeof window !== "undefined") {
    pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ""}/pdf.worker.min.mjs`
}

/** pdf.js transfers bytes to the worker and detaches the buffer — copy so React Strict Mode / re-renders stay valid. */
function copyPdfBytes(data) {
    if (data instanceof ArrayBuffer) {
        return data.slice(0)
    }
    const u8 = data instanceof Uint8Array ? data : new Uint8Array(data)
    return u8.buffer.slice(u8.byteOffset, u8.byteOffset + u8.byteLength)
}

/**
 * Renders a PDF to <canvas> pages with pdf.js instead of handing the file to the
 * browser's built-in PDF plugin.
 *
 * Why: an <iframe src="...pdf"> uses the browser's native PDF viewer, which has its
 * own right-click context menu (Print / Save / Download) that lives in a different
 * document context — a contextmenu handler on the surrounding React tree can't see
 * or block it, so "view only" documents could be exfiltrated via right-click → Print
 * → Save as PDF. Rendering into canvases keeps everything in the same DOM so:
 *   - contextmenu events bubble normally and can be prevented (no native menu)
 *   - there's no browser PDF toolbar/menu with a Print or Download button
 *   - Ctrl/Cmd+P and Ctrl/Cmd+S are intercepted while the viewer is mounted
 *
 * This does not (and cannot) stop OS-level screenshots or screen recording — treat
 * it as a deterrent for casual copying, not a DRM guarantee.
 */
export default function SecurePdfCanvas({ data }) {
    const hostRef = useRef(null)
    const pdfRef = useRef(null)
    const [status, setStatus] = useState("loading")
    const [error, setError] = useState("")

    useEffect(() => {
        let cancelled = false
        const host = hostRef.current
        if (!host || !data) return undefined

        async function run() {
            setStatus("loading")
            setError("")
            host.replaceChildren()

            try {
                const pdfBytes = copyPdfBytes(data)
                const loadingTask = pdfjsLib.getDocument({ data: new Uint8Array(pdfBytes) })
                const pdf = await loadingTask.promise
                if (cancelled) {
                    pdf.destroy()
                    return
                }
                pdfRef.current = pdf

                const cssScale = 1.25
                const pixelRatio = window.devicePixelRatio || 1

                for (let i = 1; i <= pdf.numPages; i++) {
                    if (cancelled) break
                    const page = await pdf.getPage(i)
                    const viewport = page.getViewport({ scale: cssScale })
                    const canvas = document.createElement("canvas")
                    const ctx = canvas.getContext("2d", { alpha: false })
                    canvas.width = Math.floor(viewport.width * pixelRatio)
                    canvas.height = Math.floor(viewport.height * pixelRatio)
                    canvas.style.width = `${viewport.width}px`
                    canvas.style.height = `${viewport.height}px`
                    canvas.style.display = "block"
                    canvas.style.margin = "0 auto 20px auto"
                    canvas.style.boxShadow = "0 2px 12px rgba(0,0,0,0.2)"
                    canvas.style.background = "#fff"
                    canvas.draggable = false
                    canvas.style.webkitUserDrag = "none"
                    host.appendChild(canvas)

                    const transform = pixelRatio !== 1 ? [pixelRatio, 0, 0, pixelRatio, 0, 0] : null
                    await page.render({
                        canvasContext: ctx,
                        viewport,
                        ...(transform ? { transform } : {}),
                    }).promise
                }

                if (!cancelled) setStatus("ready")
            } catch (e) {
                if (!cancelled) {
                    setError(e?.message || "Failed to load document")
                    setStatus("error")
                }
            }
        }

        run()

        return () => {
            cancelled = true
            if (pdfRef.current) {
                try {
                    pdfRef.current.destroy()
                } catch {
                    /* ignore */
                }
                pdfRef.current = null
            }
        }
    }, [data])

    // Best-effort: block the most common print/save shortcuts while the viewer is open.
    // Browsers can still allow printing via their own menu bar — this is a deterrent,
    // not a guarantee. Right-click is blocked separately via onContextMenu below.
    useEffect(() => {
        const blockShortcuts = (e) => {
            const key = e.key?.toLowerCase()
            if ((e.ctrlKey || e.metaKey) && (key === "p" || key === "s")) {
                e.preventDefault()
                e.stopPropagation()
            }
        }
        window.addEventListener("keydown", blockShortcuts, true)
        return () => window.removeEventListener("keydown", blockShortcuts, true)
    }, [])

    return (
        <div
            className="secure-pdf-canvas"
            onContextMenu={e => e.preventDefault()}
            onDragStart={e => e.preventDefault()}
            style={{
                flex: 1,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                background: "#525659",
                userSelect: "none",
                position: "relative",
                minHeight: 0,
            }}
        >
            {status === "loading" && (
                <div style={{
                    position: "absolute", inset: 0, zIndex: 1, display: "flex",
                    flexDirection: "column", gap: 16, alignItems: "center", justifyContent: "center",
                    color: "#e2e8f0", fontSize: 15, pointerEvents: "none",
                }}>
                    <div className="secure-pdf-canvas__spinner" aria-hidden="true" />
                    <span>Loading document…</span>
                </div>
            )}
            {status === "error" && error && (
                <div style={{
                    padding: 24, color: "#fecaca", textAlign: "center", fontSize: 14,
                }}>
                    {error}
                </div>
            )}
            <div
                ref={hostRef}
                style={{
                    flex: 1,
                    overflow: "auto",
                    padding: "24px 16px",
                    minHeight: 0,
                }}
            />
        </div>
    )
}
